import React, { ChangeEvent, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Autocomplete,
  Button,
  CardMedia,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Input,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from "@mui/material";

import "react-phone-input-2/lib/bootstrap.css";
import CloseIcon from "@mui/icons-material/Close";

import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

import { UploadMedia } from "../../utils/uploadMedia";
import { showError, showToast } from "../../constants/toasts";
import { isValidInput } from "../../utils/validations";

import { useFormik } from "formik";
import * as Yup from "yup";
import {
  useAddCategoryMutation,
  useGetCategoryByIdQuery,
  useLazyGetCategoryByIdQuery,
  useUpdateCategoryMutation,
} from "../../service/category";
import usePermission from "../../hooks/usePermission";

const validationSchema = Yup.object({
  title: Yup.string()
    .required("Title is required")
    .min(2, "Title must be at least 2 characters")
    .max(100, "Title must be at most 100 characters"),
  serviceFee: Yup.number()
    .required("Service fee is required")
    .min(0, "Service fee cannot be negative")
    .max(10000, "Service fee is too high"),
  description: Yup.string().max(
    500,
    "Description must be at most 500 characters"
  ),
  image: Yup.string().required("Category image is required"),
  isFeatured: Yup.boolean(),
});

const AddCategory = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isAddPromotion = location.pathname === "/manage-categories/add";

  const { id } = useParams();
  const { isAdd } = usePermission({
    label: "Manage Product Categories",
    page_permission: id ? "isEdit" : "isAdd",
  });

  const [image, setImage] = useState<string>("");
  const [fileName, setFileName] = useState<string>("");
  const [isLoadingImage, setIsLoadingImage] = useState(false);

  const isAddCategory = location.pathname === "/manage-categories/add";

  // API hooks
  const [getCategoryById, { isLoading: isLoadingCategory }] =
    useLazyGetCategoryByIdQuery();
  const [addCategory, { isLoading: isAdding }] = useAddCategoryMutation();
  const [updateCategory, { isLoading: isUpdating }] =
    useUpdateCategoryMutation();

  const formik = useFormik({
    initialValues: {
      title: "",
      serviceFee: "",
      description: "",
      image: "",
      isFeatured: false,
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        const categoryData = {
          name: values.title,
          serviceFee: parseFloat(values.serviceFee),
          description: values.description || undefined,
          image: values.image,
          isFeatured: values.isFeatured,
          // isActive: true, // Default to active when creating
        };

        let response;

        if (isAddCategory) {
          // Add new category
          response = await addCategory(categoryData).unwrap();
          if (response.code === 200) {
            showToast("Category added successfully");
            navigate("/manage-categories");
          }
        } else {
          // Update existing category
          response = await updateCategory({
            id: id as string,
            body: categoryData,
          }).unwrap();
          if (response.code === 200) {
            showToast("Category updated successfully");
            navigate("/manage-categories");
          }
        }
      } catch (error: any) {
        console.error("Error saving category:", error);
        showError(
          error?.data?.message ||
            `Failed to ${isAddCategory ? "add" : "update"} category`
        );
      }
    },
  });

  const handleImageUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || !files[0]) return;

    const file = files[0];

    // Validate file type
    if (!file.type.includes("image")) {
      showError("This field only accepts images.");
      return;
    }

    // Validate file size (e.g., 5MB limit)
    // if (file.size > 5 * 1024 * 1024) {
    //   showError("Image size should be less than 5MB");
    //   return;
    // }

    try {
      setIsLoadingImage(true);

      // Upload image to your server
      const res = await UploadMedia(file);
      if (res?.code === 200) {
        const imageUrl = res.data;
        setImage(imageUrl);
        formik.setFieldValue("image", imageUrl);
        setFileName(file.name);
        showToast("Image uploaded successfully.");
      } else {
        showError(res?.message || "Failed to upload image");
      }
    } catch (error) {
      console.error("Error uploading image:", error);
      showError("Error uploading image. Please try again.");
    } finally {
      setIsLoadingImage(false);
    }
  };

  // Fetch category data for editing
  useEffect(() => {
    if (!isAddCategory && id) {
      fetchCategoryData();
    }
  }, [id, isAddCategory]);

  const fetchCategoryData = async () => {
    try {
      const response = await getCategoryById(id as string).unwrap();
      if (response.code === 200) {
        const category = response.data;

        // Set form values
        formik.setValues({
          title: category.name || "",
          serviceFee: category.serviceFee?.toString() || "",
          description: category.description || "",
          image: category.image || "",
          isFeatured: category.isFeatured || false,
        });

        // Set image if exists
        if (category.image) {
          setImage(category.image);
        }
      }
    } catch (error: any) {
      console.error("Error fetching category data:", error);
      showError(error?.data?.message || "Failed to fetch category data");
      navigate("/manage-categories");
    }
  };

  const removeImage = () => {
    setImage("");
    setFileName("");
    formik.setFieldValue("image", "");
  };

  const isLoading = isAdding || isUpdating || isLoadingCategory;

  return (
    <>
      <div className="main_title">
        <h1>{isAddPromotion ? "Add " : "Edit "} Product Category</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> -{" "}
          <button onClick={() => navigate("/manage-categories")}>
            Manage Product Categories
          </button>{" "}
          - {isAddPromotion ? "Add " : "Edit "} Product Category
        </p>
      </div>

      <div className="cards">
        <div className="form">
          <form onSubmit={formik.handleSubmit}>
            <div className="gap_p">
              <div className="control_group w_100">
                <div className="gap_p">
                  <div className="control_group w_33">
                    <label htmlFor="icon-button-file">Upload Image</label>
                    {image ? (
                      <div className="upload_image v2 no_radius">
                        <div className="upload_image_holder">
                          <figure>
                            <CardMedia
                              component="img"
                              image={image}
                              alt="Uploaded profile photo"
                            />
                          </figure>
                          <IconButton
                            aria-label="Remove uploaded image"
                            onClick={removeImage}
                            disabled={isLoadingImage}
                            size="small"
                          >
                            <CloseIcon />
                          </IconButton>
                        </div>
                        {formik.touched.image && formik.errors.image && (
                          <Typography
                            variant="caption"
                            color="error"
                            sx={{ display: "block", mt: 0.5 }}
                          >
                            {formik.errors.image}
                          </Typography>
                        )}
                      </div>
                    ) : (
                      <label
                        className="upload_image v2 no_radius upload_image_error"
                        htmlFor="icon-button-file"
                      >
                        <Input
                          sx={{ display: "none" }}
                          id="icon-button-file"
                          type="file"
                          inputProps={{ accept: "image/png,image/jpeg" }}
                          onChange={handleImageUpload}
                          disabled={isLoadingImage}
                        />
                        <span className="upload_image_holder">
                          <figure className="add_img">
                            {isLoadingImage ? (
                              <CircularProgress size={40} />
                            ) : (
                              <>
                                <img
                                  src="/static/images/placeholder.png"
                                  alt="placeholder"
                                />
                                <span className="upload_img_icon">
                                  <img
                                    src="/static/images/edit2_icon.svg"
                                    alt="Upload"
                                  />
                                </span>
                              </>
                            )}
                          </figure>
                        </span>
                        {formik.touched.image && formik.errors.image && (
                          <Typography
                            variant="caption"
                            color="error"
                            sx={{ display: "block", mt: 1.5 }}
                          >
                            {formik.errors.image}
                          </Typography>
                        )}
                      </label>
                    )}
                  </div>
                </div>
              </div>

              <div className="control_group w_33">
                <label htmlFor="Title">Title</label>
                <TextField
                  id="title"
                  name="title"
                  placeholder="Enter Title"
                  hiddenLabel
                  fullWidth
                  value={formik.values.title}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (isValidInput(value) && value.length <= 100) {
                      formik.handleChange(e);
                    }
                  }}
                  onBlur={formik.handleBlur}
                  error={formik.touched.title && Boolean(formik.errors.title)}
                  helperText={formik.touched.title && formik.errors.title}
                  disabled={isLoading}
                />
              </div>
              <div className="control_group w_33">
                <label htmlFor="Service_Fee">Service Fee</label>
                <TextField
                  id="serviceFee"
                  name="serviceFee"
                  placeholder="Enter Service Fee"
                  hiddenLabel
                  fullWidth
                  type="text"
                  inputProps={{
                    min: 0,
                    max: 10000,
                    step: "0.01",
                  }}
                  value={formik.values.serviceFee}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Allow only numbers and decimal point
                    if (/^\d*\.?\d*$/.test(value) && value.length <= 10) {
                      formik.handleChange(e);
                    }
                  }}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.serviceFee &&
                    Boolean(formik.errors.serviceFee)
                  }
                  helperText={
                    formik.touched.serviceFee && formik.errors.serviceFee
                  }
                  disabled={isLoading}
                />
              </div>

              <div className="control_group ">
                <label htmlFor="Description">Description</label>
                <TextField
                  id="description"
                  name="description"
                  minRows={5}
                  multiline
                  placeholder="Enter Description"
                  hiddenLabel
                  fullWidth
                  value={formik.values.description}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value.length <= 500) {
                      formik.handleChange(e);
                    }
                  }}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.description &&
                    Boolean(formik.errors.description)
                  }
                  helperText={
                    formik.touched.description
                      ? formik.errors.description
                      : `${formik.values.description.length}/500 characters`
                  }
                  disabled={isLoading}
                />
              </div>
              <div className="control_group w_33">
                <FormControlLabel
                  control={
                    <Checkbox
                      name="isFeatured"
                      checked={formik.values.isFeatured}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      disabled={isLoading}
                    />
                  }
                  label="Mark as Featured Category"
                />
              </div>
            </div>

            <div className="form_btn">
              <Button
                className="btn btn_primary"
                type="submit"
                disabled={isLoading}
                startIcon={isLoading ? <CircularProgress size={16} /> : null}
              >
                {isLoading
                  ? isAddCategory
                    ? "Adding..."
                    : "Updating..."
                  : isAddCategory
                  ? "Add Category"
                  : "Update Category"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default AddCategory;
